// Per-company configuration for the "חברות" (companies) settings tab.
// Insurance-company ids are the fixed seeded GUID constants from the backend
// (InsuranceCompanyModel) so the mapping is environment-independent.
//
// Each company has a Routes table and an Agents table. The columns common to
// every company are produced by the column factories below; companies that
// need extra fields add "specific detail" columns (stored on the row's
// specificDetails[] as { name, data }).

import {
    InsuranceCompanyRouteModel,
    AgentDetailsModel,
    SpecificInsuranceCompanyDetails,
} from '../../models/superbit-api-models';
import { SelectOption, YES_NO_OPTIONS } from './simulatorSettings.constants';

export const COMPANY_IDS = {
    AYALON: '1c9bc612-82bb-4695-b8c9-94d858dc745a',
    HAREL: '2c9057e2-48bc-470c-a9b5-a7ed314188aa',
    SHOMERA: 'c954609e-30db-4f69-a361-e4ae0acfea37',
    SHLOMO: '3a08fee4-8879-4e23-905d-72fa241568dc',
    MIGDAL: 'ae7b1e1a-c9ce-46a7-9c0f-1fb5a2a5f7c3',
    HACHSHARA: 'd999420e-127b-4ede-8b94-497eb49e7579',
    CLAL: 'c09ac974-9d0c-497c-8962-13c7d0687ea7',
    FNX: 'fc39ad17-fa18-4ce0-bd3e-834df336162d',
    MENORA: 'ec6010ce-8bbe-4a84-b607-ebba638ff75a',
    ANKOR: 'a6ccf8f5-1bd7-4f12-b115-08774b536760',
} as const;

export interface CompanyTab {
    key: string;
    label: string;
    id: string;
    /** false while the company is still a placeholder (built in a later slice). */
    ready: boolean;
}

export const COMPANY_TABS: CompanyTab[] = [
    { key: 'ayalon', label: 'איילון', id: COMPANY_IDS.AYALON, ready: true },
    { key: 'harel', label: 'הראל', id: COMPANY_IDS.HAREL, ready: true },
    { key: 'shomera', label: 'שומרה', id: COMPANY_IDS.SHOMERA, ready: true },
    { key: 'shlomo', label: 'שלמה', id: COMPANY_IDS.SHLOMO, ready: true },
    { key: 'migdal', label: 'מגדל', id: COMPANY_IDS.MIGDAL, ready: true },
    { key: 'hachshara', label: 'הכשרה', id: COMPANY_IDS.HACHSHARA, ready: true },
    { key: 'clal', label: 'כלל', id: COMPANY_IDS.CLAL, ready: true },
    { key: 'fnx', label: 'הפניקס', id: COMPANY_IDS.FNX, ready: true },
    { key: 'menora', label: 'מנורה', id: COMPANY_IDS.MENORA, ready: true },
    { key: 'ankor', label: 'אנקור', id: COMPANY_IDS.ANKOR, ready: true },
];

// --- shared option lists ---------------------------------------------------

export const STATUS_OPTIONS: SelectOption[] = [
    { value: 0, label: 'פעיל' }, // ActivityStatus.Active
    { value: 1, label: 'לא פעיל' }, // ActivityStatus.Inactive
];

// InsuranceTypes (route "סוג מסלול"): '' = null = both/all
export const INSURANCE_TYPE_OPTIONS: SelectOption[] = [
    { value: '', label: 'הכל' },
    { value: 0, label: 'מקיף' },
    { value: 1, label: "צד ג'" },
];

// AyalonProd.ANAF — stored as the numeric value string (Enum.Parse-compatible).
export const ANAF_OPTIONS: SelectOption[] = [
    { value: 0, label: 'UNDEFINED' },
    { value: 1, label: 'CASCO' },
    { value: 2, label: 'OTODIL' },
    { value: 3, label: 'COLLECTIVE' },
];

// ShomeraRouteType — stored as the numeric value string.
export const SHOMERA_ROUTE_TYPE_OPTIONS: SelectOption[] = [
    { value: 1, label: 'C' },
    { value: 2, label: 'C#' },
    { value: 3, label: 'C רכב משני' },
    { value: 4, label: '730' },
    { value: 5, label: '730 טריפל' },
    { value: 6, label: '742' },
];

// PaymentMethodType — stored as the numeric value string.
export const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
    { value: 0, label: 'כרטיס אשראי' },
    { value: 1, label: 'המחאה' },
    { value: 2, label: 'העברה בנקאית' },
    { value: 3, label: 'אחר' },
];

// --- column model ----------------------------------------------------------

export type FieldType =
    | 'company'
    | 'text'
    | 'number'
    | 'select'
    | 'status'
    | 'radio'
    | 'dynamicSelect';

export interface ColumnDef {
    key: string;
    label: string;
    type: FieldType;
    /** static options for type 'select' */
    options?: SelectOption[];
    /** key into EditableTable's dynamicOptions map, for type 'dynamicSelect' */
    optionsKey?: string;
    placeholder?: string;
    get: (row: any) => any;
    set: (row: any, value: any) => void;
}

// --- specificDetails helpers ----------------------------------------------

export const getSpecific = (row: any, name: string): string => {
    const list: SpecificInsuranceCompanyDetails[] | undefined = row?.specificDetails;
    const found = list?.find((d) => d.name === name);
    return found?.data ?? '';
};

export const setSpecific = (row: any, name: string, value: any): void => {
    if (!Array.isArray(row.specificDetails)) row.specificDetails = [];
    const found = row.specificDetails.find(
        (d: SpecificInsuranceCompanyDetails) => d.name === name
    );
    const data = value == null ? '' : String(value);
    if (found) {
        found.data = data;
    } else {
        row.specificDetails.push(
            SpecificInsuranceCompanyDetails.fromJS({ name, data, title: name })
        );
    }
};

// --- reusable common columns ----------------------------------------------

const companyColumn = (): ColumnDef => ({
    key: 'insuranceCompanyId',
    label: 'חברה',
    type: 'company',
    get: (r) => (r.insuranceCompanyId ?? '').toString().toLowerCase(),
    set: (r, v) => {
        r.insuranceCompanyId = v;
    },
});

const statusColumn = (): ColumnDef => ({
    key: 'status',
    label: 'סטטוס',
    type: 'status',
    options: STATUS_OPTIONS,
    get: (r) => r.status ?? 0,
    set: (r, v) => {
        r.status = Number(v);
    },
});

const routeIdColumn = (label = 'מסלול'): ColumnDef => ({
    key: 'routeId',
    label,
    type: 'number',
    placeholder: label,
    get: (r) => r.routeId ?? '',
    set: (r, v) => {
        r.routeId = v === '' ? undefined : Number(v);
    },
});

const commentColumn = (): ColumnDef => ({
    key: 'comment',
    label: 'הערה',
    type: 'text',
    placeholder: 'הערה',
    get: (r) => r.comment ?? '',
    set: (r, v) => {
        r.comment = v;
    },
});

const specificTextColumn = (
    key: string,
    label: string,
    name: string,
    placeholder?: string
): ColumnDef => ({
    key,
    label,
    type: 'text',
    placeholder: placeholder ?? label,
    get: (r) => getSpecific(r, name),
    set: (r, v) => setSpecific(r, name, v),
});

const specificSelectColumn = (
    key: string,
    label: string,
    name: string,
    options: SelectOption[]
): ColumnDef => ({
    key,
    label,
    type: 'select',
    options,
    get: (r) => getSpecific(r, name),
    set: (r, v) => setSpecific(r, name, v),
});

// generic numeric model field (e.g. compulsoryRouteId)
const numberColumn = (key: string, label: string): ColumnDef => ({
    key,
    label,
    type: 'number',
    placeholder: label,
    get: (r) => r[key] ?? '',
    set: (r, v) => {
        r[key] = v === '' ? undefined : Number(v);
    },
});

// generic enum/numeric model field rendered as a select (e.g. containsRider, insuranceType)
const modelSelectColumn = (
    key: string,
    label: string,
    options: SelectOption[]
): ColumnDef => ({
    key,
    label,
    type: 'select',
    options,
    get: (r) => r[key] ?? '',
    set: (r, v) => {
        r[key] = v === '' ? undefined : Number(v);
    },
});

// agent common columns
const agentTextColumn = (key: string, label: string): ColumnDef => ({
    key,
    label,
    type: 'text',
    placeholder: label,
    get: (r) => r[key] ?? '',
    set: (r, v) => {
        r[key] = v;
    },
});

// single-select-across-rows radio, stored on specificDetails as 'checked' / ''
const radioColumn = (key: string, label: string, name: string): ColumnDef => ({
    key,
    label,
    type: 'radio',
    get: (r) => getSpecific(r, name),
    set: (r, v) => setSpecific(r, name, v),
});

// select whose options come from the live table data (e.g. Hachshara agents)
const dynamicSelectColumn = (
    key: string,
    label: string,
    name: string,
    optionsKey: string
): ColumnDef => ({
    key,
    label,
    type: 'dynamicSelect',
    optionsKey,
    get: (r) => (getSpecific(r, name) || '').toLowerCase(),
    set: (r, v) => setSpecific(r, name, v),
});

// --- per-company configuration --------------------------------------------

export interface CompanyConfig {
    routeColumns: ColumnDef[];
    agentColumns: ColumnDef[];
    newRoute: (companyId: string) => any;
    newAgent: (companyId: string) => any;
}

const baseNewRoute = (companyId: string) =>
    InsuranceCompanyRouteModel.fromJS({
        insuranceCompanyId: companyId,
        routeId: 0,
        status: 0,
        comment: '',
        specificDetails: [],
    });

const baseNewAgent = (companyId: string) =>
    AgentDetailsModel.fromJS({
        insuranceCompanyId: companyId,
        status: 0,
        specificDetails: [],
    });

// Hachshara layout — also reused as-is for Clal / Fnx / Menora / Ankor.
const hachsharaConfig: CompanyConfig = {
    routeColumns: [
        companyColumn(),
        routeIdColumn('ענף'),
        numberColumn('compulsoryRouteId', 'ענף חובה'),
        statusColumn(),
        dynamicSelectColumn('hachsharaAgent', 'סוכן', 'HachsharaAgent', 'hachsharaAgents'),
        specificSelectColumn(
            'hachsharaPaymentMethod',
            'אמצעי תשלום',
            'HachsharaPaymentMethod',
            PAYMENT_METHOD_OPTIONS
        ),
        modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
        modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
        commentColumn(),
    ],
    agentColumns: [
        companyColumn(),
        agentTextColumn('agentNumber', 'מספר סוכן'),
        statusColumn(),
        agentTextColumn('title', 'הערה'),
    ],
    newRoute: (companyId: string) =>
        InsuranceCompanyRouteModel.fromJS({
            insuranceCompanyId: companyId,
            routeId: 0,
            status: 0,
            comment: '',
            specificDetails: [],
            containsRider: 1,
            insuranceType: 0,
        }),
    newAgent: baseNewAgent,
};

// Clal — custom route layout (חברה / ענף / ענף חובה / סטטוס / קוד / כולל ריידר / סוג מסלול / הערה)
const clalConfig: CompanyConfig = {
    routeColumns: [
        companyColumn(),
        routeIdColumn('ענף'),
        numberColumn('compulsoryRouteId', 'ענף חובה'),
        statusColumn(),
        specificTextColumn('clalCode', 'קוד', 'ClalCode'),
        modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
        modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
        commentColumn(),
    ],
    agentColumns: [
        companyColumn(),
        specificTextColumn('clalApplicationKey', 'מזהה אפליקציה', 'ApplicationKey'),
        agentTextColumn('userName', 'שם משתמש'),
        agentTextColumn('agentNumber', 'מספר סוכן'),
        agentTextColumn('password', 'סיסמה'),
        agentTextColumn('userName', 'ת.ז.'),
        statusColumn(),
        agentTextColumn('title', 'הערה'),
    ],
    newRoute: (companyId: string) =>
        InsuranceCompanyRouteModel.fromJS({
            insuranceCompanyId: companyId,
            routeId: 0,
            status: 0,
            comment: '',
            specificDetails: [],
            containsRider: 1, // YesNo.No
            insuranceType: 0, // Comprehensive
        }),
    newAgent: baseNewAgent,
};

// Fnx (הפניקס) — like Clal but with cover type (FnxCoverType) instead of code
const fnxConfig: CompanyConfig = {
    routeColumns: [
        companyColumn(),
        routeIdColumn('ענף'),
        numberColumn('compulsoryRouteId', 'ענף חובה'),
        statusColumn(),
        specificTextColumn('fnxCoverType', 'סוג כיסוי', 'FnxCoverType'),
        modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
        modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
        commentColumn(),
    ],
    agentColumns: [
        companyColumn(),
        agentTextColumn('agentNumber', 'מספר סוכן'),
        agentTextColumn('userName', 'מספר סניף'),
        statusColumn(),
        agentTextColumn('title', 'הערה'),
    ],
    newRoute: (companyId: string) =>
        InsuranceCompanyRouteModel.fromJS({
            insuranceCompanyId: companyId,
            routeId: 0,
            status: 0,
            comment: '',
            specificDetails: [],
            containsRider: 1,
            insuranceType: 0,
        }),
    newAgent: baseNewAgent,
};

// Menora (מנורה) — company / route / compulsory route / status / route type / comment
const menoraConfig: CompanyConfig = {
    routeColumns: [
        companyColumn(),
        routeIdColumn('ענף'),
        numberColumn('compulsoryRouteId', 'ענף חובה'),
        statusColumn(),
        modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
        commentColumn(),
    ],
    agentColumns: [
        companyColumn(),
        agentTextColumn('userName', 'שם משתמש'),
        agentTextColumn('agentNumber', 'מספר סוכן'),
        statusColumn(),
        agentTextColumn('title', 'הערה'),
    ],
    newRoute: (companyId: string) =>
        InsuranceCompanyRouteModel.fromJS({
            insuranceCompanyId: companyId,
            routeId: 0,
            status: 0,
            comment: '',
            specificDetails: [],
            insuranceType: 0,
        }),
    newAgent: baseNewAgent,
};

// Ankor (אנקור) — company / route / status / compulsory route / contains rider / route type / comment
const ankorConfig: CompanyConfig = {
    routeColumns: [
        companyColumn(),
        routeIdColumn('מסלול'),
        statusColumn(),
        numberColumn('compulsoryRouteId', 'ענף חובה'),
        modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
        modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
        commentColumn(),
    ],
    agentColumns: [
        companyColumn(),
        agentTextColumn('agentNumber', 'מספר סוכן'),
        statusColumn(),
        agentTextColumn('title', 'הערה'),
    ],
    newRoute: (companyId: string) =>
        InsuranceCompanyRouteModel.fromJS({
            insuranceCompanyId: companyId,
            routeId: 0,
            status: 0,
            comment: '',
            specificDetails: [],
            containsRider: 1,
            insuranceType: 0,
        }),
    newAgent: baseNewAgent,
};

export const COMPANY_CONFIGS: Record<string, CompanyConfig> = {
    [COMPANY_IDS.AYALON]: {
        routeColumns: [
            companyColumn(),
            routeIdColumn('מסלול'),
            statusColumn(),
            specificSelectColumn('ayalonBranch', 'ענף', 'AyalonBranch', ANAF_OPTIONS),
            numberColumn('compulsoryRouteId', 'ענף חובה'),
            specificTextColumn('ayalonPassword', 'סיסמה', 'AyalonPassword', 'סיסמה'),
            modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
            modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
            commentColumn(),
        ],
        agentColumns: [
            companyColumn(),
            agentTextColumn('userName', 'שם משתמש'),
            agentTextColumn('agentNumber', 'מספר סוכן'),
            agentTextColumn('password', 'סיסמה'),
            statusColumn(),
            agentTextColumn('title', 'הערה'),
        ],
        newRoute: (companyId: string) =>
            InsuranceCompanyRouteModel.fromJS({
                insuranceCompanyId: companyId,
                routeId: 0,
                status: 0,
                comment: '',
                specificDetails: [],
                containsRider: 1,
                insuranceType: 0,
            }),
        newAgent: baseNewAgent,
    },

    [COMPANY_IDS.HAREL]: {
        routeColumns: [
            companyColumn(),
            routeIdColumn('מסלול'),
            numberColumn('compulsoryRouteId', 'ענף חובה'),
            statusColumn(),
            modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
            modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
            commentColumn(),
        ],
        agentColumns: [
            companyColumn(),
            agentTextColumn('userName', 'שם משתמש'),
            agentTextColumn('agentNumber', 'מספר סוכן'),
            agentTextColumn('password', 'סיסמה'),
            specificTextColumn('harelClientName', 'שם חברה', 'HarelClientName'),
            specificTextColumn('harelCollectiveNumber', 'מס קולקטיב', 'HarelCollectiveNumber'),
            specificTextColumn('harelUserCode', 'קוד משתמש', 'HarelUserCode'),
            statusColumn(),
            agentTextColumn('title', 'הערה'),
        ],
        newRoute: (companyId: string) =>
            InsuranceCompanyRouteModel.fromJS({
                insuranceCompanyId: companyId,
                routeId: 0,
                status: 0,
                comment: '',
                specificDetails: [],
                containsRider: 1,
                insuranceType: 0,
            }),
        newAgent: baseNewAgent,
    },

    [COMPANY_IDS.SHOMERA]: {
        routeColumns: [
            companyColumn(),
            routeIdColumn('ענף'),
            numberColumn('compulsoryRouteId', 'ענף חובה'),
            specificTextColumn('shomeraCoverageType', 'סוג כיסוי', 'ShomeraCoverageType'),
            statusColumn(),
            specificTextColumn('shomeraProductNumber', 'מוצר ממ"ש', 'ShomeraProductNumber'),
            specificSelectColumn(
                'shomeraRouteType',
                'סוג ענף',
                'ShomeraRouteType',
                SHOMERA_ROUTE_TYPE_OPTIONS
            ),
            modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
            commentColumn(),
        ],
        agentColumns: [
            companyColumn(),
            radioColumn('shomeraMainAgent', 'סוכן ראשי', 'ShomeraMainAgent'),
            agentTextColumn('agentNumber', 'מספר סוכן'),
            statusColumn(),
            agentTextColumn('title', 'הערה'),
        ],
        newRoute: (companyId: string) =>
            InsuranceCompanyRouteModel.fromJS({
                insuranceCompanyId: companyId,
                routeId: 0,
                status: 0,
                comment: '',
                specificDetails: [],
                insuranceType: 0,
            }),
        newAgent: baseNewAgent,
    },

    [COMPANY_IDS.SHLOMO]: {
        routeColumns: [
            companyColumn(),
            routeIdColumn('ענף'),
            numberColumn('compulsoryRouteId', 'ענף חובה'),
            specificTextColumn('shlomoCollectiveNumber', 'קולקטיב', 'ShlomoCollectiveNumber'),
            statusColumn(),
            modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
            modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
            commentColumn(),
        ],
        agentColumns: [
            companyColumn(),
            agentTextColumn('agentNumber', 'מספר סוכן'),
            statusColumn(),
            agentTextColumn('title', 'הערה'),
        ],
        newRoute: (companyId: string) =>
            InsuranceCompanyRouteModel.fromJS({
                insuranceCompanyId: companyId,
                routeId: 0,
                status: 0,
                comment: '',
                specificDetails: [],
                containsRider: 1,
                insuranceType: 0,
            }),
        newAgent: baseNewAgent,
    },

    [COMPANY_IDS.MIGDAL]: {
        routeColumns: [
            companyColumn(),
            routeIdColumn('מסלול'),
            numberColumn('compulsoryRouteId', 'ענף חובה'),
            specificTextColumn('migdalRouteName', 'שם המסלול', 'MigdalRouteName'),
            specificTextColumn('migdalRouteComments', 'הערות למסלול', 'MigdalRouteComments'),
            statusColumn(),
            modelSelectColumn('containsRider', 'כולל ריידר', YES_NO_OPTIONS),
            modelSelectColumn('insuranceType', 'סוג מסלול', INSURANCE_TYPE_OPTIONS),
            commentColumn(),
        ],
        agentColumns: [
            companyColumn(),
            agentTextColumn('userName', 'שם משתמש'),
            agentTextColumn('agentNumber', 'מספר סוכן'),
            specificTextColumn('migdalRegion', 'מחוז', 'MigdalRegion'),
            agentTextColumn('password', 'סיסמה'),
            statusColumn(),
            agentTextColumn('title', 'הערה'),
        ],
        newRoute: (companyId: string) =>
            InsuranceCompanyRouteModel.fromJS({
                insuranceCompanyId: companyId,
                routeId: 0,
                status: 0,
                comment: '',
                specificDetails: [],
                containsRider: 1,
                insuranceType: 0,
            }),
        newAgent: baseNewAgent,
    },

    // Hachshara, and the companies copied from it (Clal / Fnx / Menora / Ankor).
    [COMPANY_IDS.HACHSHARA]: hachsharaConfig,
    [COMPANY_IDS.CLAL]: clalConfig,
    [COMPANY_IDS.FNX]: fnxConfig,
    [COMPANY_IDS.MENORA]: menoraConfig,
    [COMPANY_IDS.ANKOR]: ankorConfig,
};
